import React, { useState, useEffect } from "react";
import {
  FaEye,
  FaEyeSlash,
  FaKey,
  FaUserCircle,
  FaUser,
  FaMapMarkerAlt,
  FaRegAddressCard,
  FaAt,
  FaEnvelope,
  FaEnvelopeOpen,
} from "react-icons/fa";
import { MdEmail, MdPhone, MdLocationOn } from "react-icons/md";
import toast, { Toaster } from "react-hot-toast";
import { RotatingLines } from "react-loader-spinner";

const ManageProfile = () => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [currentPasswordVisible, setCurrentPasswordVisible] = useState(false);
  const [photoURL, setPhotoURL] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [userInfo, setUserInfo] = useState({
    firstName: "",
    surname: "",
    username: "",
    email: "",
    phoneNumber: "",
    address: "",
    postcode: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const fetchUserDetails = async () => {
    const token = localStorage.getItem("token");
    console.log("Fetching user details with token:", token);

    if (!token) {
      console.error("No token found in local storage.");
      toast.error("No token found. Please log in again.");
      return;
    }

    try {
      const response = await fetch("https://backend.pmc-group.uk/api/me", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Response status:", response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const userDetails = await response.json();
      console.log("Fetched user details:", userDetails);

      setUserInfo({
        firstName: capitalize(userDetails.first_name),
        surname: capitalize(userDetails.last_name),
        username: `${capitalize(userDetails.first_name)}.${capitalize(
          userDetails.last_name
        )}`,
        email: userDetails.email || "",
        phoneNumber: userDetails.phone_number || "",
        address: capitalize(userDetails.address),
        postcode: userDetails.postal_code || "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Error fetching user details:", error);
      toast.error("Failed to fetch user details. Please try again.");
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserInfo((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (upload) => {
        setPhotoURL(upload.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoURL("");
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const toggleConfirmPasswordVisibility = () => {
    setConfirmPasswordVisible(!confirmPasswordVisible);
  };

  const toggleCurrentPasswordVisibility = () => {
    setCurrentPasswordVisible(!currentPasswordVisible);
  };

  const validatePasswords = () => {
    const { currentPassword, newPassword, confirmPassword } = userInfo;
    const errors = {};

    if (!currentPassword) {
      errors.currentPassword = "Current password is required.";
    }
    if (!newPassword) {
      errors.newPassword = "New password is required.";
    }
    if (newPassword && newPassword.length < 8) {
      errors.newPassword = "New password must be at least 8 characters.";
    }
    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your new password.";
    }
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      errors.confirmPassword =
        "New password and confirm password do not match.";
    }

    return errors;
  };

  const handleSaveChanges = async () => {
    const passwordErrors = validatePasswords();
    if (Object.keys(passwordErrors).length > 0) {
      setErrors(passwordErrors);
      return;
    }

    setLoading(true);

    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        "https://backend.pmc-group.uk/api/change_password",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            old_password: userInfo.currentPassword,
            password: userInfo.newPassword,
            password_confirmation: userInfo.confirmPassword,
          }),
        }
      );

      console.log("Password change response status:", response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.success("Password changed successfully.");
    } catch (error) {
      console.error("Error changing password:", error);
      toast.error("Failed to change password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex flex-col lg:pt-4 pb-12 h-full min-h-screen">
      <Toaster />
      <div className="md:flex-1 flex-1 py-8 px-4">
        <div className="md:max-w-9xl  w-full mx-auto p-8 md:rounded-lg bg-white">
          <div className="md:ml-12 ml-4">
            <h1 className="text-3xl font-semibold text-navBg">
              Manage Profile
            </h1>

            <div className="flex flex-col sm:flex-row pt-6 gap-4 items-center">
              <div>
                {photoURL ? (
                  <img
                    className="w-24 h-24 object-center rounded-full"
                    src={photoURL}
                    alt="profile pic"
                  />
                ) : (
                  <FaUserCircle className="w-24 h-24 text-gray-400" />
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="file"
                  id="photo-upload"
                  style={{ display: "none" }}
                  onChange={handlePhotoUpload}
                />
                <label
                  htmlFor="photo-upload"
                  className="h-10 md:w-40 w-40 rounded-lg bg-navBg text-white font-semibold text-xs flex items-center justify-center cursor-pointer hover:bg-blue-600 transition-colors duration-300"
                >
                  Upload New Photo
                </label>
                <button
                  onClick={handleRemovePhoto}
                  className="h-10 md:w-40 w-40 rounded-lg bg-transparent text-red-700 font-semibold border border-red-700 text-xs hover:text-white hover:bg-red-700 transition-colors duration-300"
                >
                  Remove Photo
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-10">
              <div>
                <div className="mb-8">
                  <label className="block font-semibold text-navBg mb-2">
                    First Name
                  </label>
                  <div className="relative">
                    <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-2xl text-gray-400" />
                    <input
                      className="w-full h-12 rounded-lg border border-gray-300 p-2 pl-10 font-medium font-Poppins bg-gray-100 text-gray-600"
                      type="text"
                      name="firstName"
                      value={userInfo.firstName}
                      onChange={handleInputChange}
                      readOnly
                    />
                  </div>
                </div>
                <div className="mb-8">
                  <label className="block font-semibold text-navBg mb-2">
                    Last Name
                  </label>
                  <div className="relative">
                    <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-2xl text-gray-400" />{" "}
                    <input
                      className="w-full h-12 rounded-lg border border-gray-300 p-2 pl-10 font-medium font-Poppins bg-gray-100 text-gray-600"
                      type="text"
                      name="surname"
                      value={userInfo.surname}
                      onChange={handleInputChange}
                      readOnly
                    />
                  </div>
                </div>
                <div className="mb-8">
                  <label className="block font-semibold text-navBg mb-2">
                    User Name
                  </label>
                  <div className="relative">
                    <FaAt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-2xl text-gray-400" />
                    <input
                      className="w-full h-12 rounded-lg border border-gray-300 p-2 pl-10 font-medium font-Poppins bg-gray-100 text-gray-600"
                      type="text"
                      name="username"
                      value={userInfo.username}
                      onChange={handleInputChange}
                      readOnly
                    />
                  </div>
                </div>
                <div className="mb-8">
                  <label className="block font-semibold text-navBg mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <MdEmail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-2xl text-gray-400" />
                    <input
                      className="w-full h-12 rounded-lg border border-gray-300 p-2 pl-10 font-medium font-Poppins bg-gray-100 text-gray-600"
                      type="email"
                      name="email"
                      value={userInfo.email}
                      onChange={handleInputChange}
                      readOnly
                    />
                  </div>
                </div>
                {/* <div className="mb-8">
                  <label className="block font-semibold text-navBg mb-2">
                    Address
                  </label>
                  <div className="relative">
                    <MdLocationOn className="absolute left-3 top-1/2 transform -translate-y-1/2 text-2xl text-gray-400" />
                    <input
                      className="w-full h-12 rounded-lg border border-gray-300 p-2 pl-10 font-medium font-Poppins bg-gray-100 text-gray-600"
                      type="text"
                      name="address"
                      value={userInfo.address}
                      onChange={handleInputChange}
                      readOnly
                    />
                  </div>
                </div> */}
              </div>
              <div>
                <div className="mb-8">
                  <label className="block font-semibold text-navBg mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <MdPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-2xl text-gray-400" />
                    <input
                      className="w-full h-12 rounded-lg border border-gray-300 p-2 pl-10 font-medium font-Poppins bg-gray-100 text-gray-600"
                      type="tel"
                      name="phoneNumber"
                      value={userInfo.phoneNumber}
                      onChange={handleInputChange}
                      readOnly
                    />
                  </div>
                </div>
                <div className="mb-8">
                  <label className="block font-semibold text-navBg mb-2">
                    Post Code
                  </label>
                  <div className="relative">
                    <FaEnvelopeOpen className="absolute left-3 top-1/2 transform -translate-y-1/2 text-2xl text-gray-400" />
                    <input
                      className="w-full h-12 rounded-lg border border-gray-300 p-2 pl-10 font-medium font-Poppins bg-gray-100 text-gray-600"
                      type="text"
                      name="postcode"
                      value={userInfo.postcode}
                      onChange={handleInputChange}
                      readOnly
                    />
                  </div>
                </div>
                <div className="mb-8">
                  <label className="block font-semibold text-navBg mb-2">
                    Current Password
                  </label>
                  {errors.currentPassword && (
                    <p className="text-red-500 text-sm">
                      {errors.currentPassword}
                    </p>
                  )}
                  <div className="relative">
                    <FaKey className="absolute left-3 top-1/2 transform -translate-y-1/2 text-xl text-gray-400" />
                    <input
                      className="w-full h-12 rounded-lg border border-gray-300 p-2 pl-10"
                      type={currentPasswordVisible ? "text" : "password"}
                      name="currentPassword"
                      value={userInfo.currentPassword}
                      onChange={handleInputChange}
                      placeholder="Current password is required"
                    />
                    <span
                      onClick={toggleCurrentPasswordVisibility}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-xl right-icon"
                    >
                      {currentPasswordVisible ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </div>
                </div>
                <div className="mb-8">
                  <label className="block font-semibold text-navBg mb-2">
                    New Password
                  </label>
                  {errors.newPassword && (
                    <p className="text-red-500 text-sm">{errors.newPassword}</p>
                  )}
                  <div className="relative">
                    <FaKey className="absolute left-3 top-1/2 transform -translate-y-1/2 text-xl text-gray-400" />
                    <input
                      className="w-full h-12 rounded-lg border border-gray-300 p-2 pl-10"
                      type={passwordVisible ? "text" : "password"}
                      name="newPassword"
                      value={userInfo.newPassword}
                      onChange={handleInputChange}
                      placeholder="Password must be at least 8 characters"
                    />
                    <span
                      onClick={togglePasswordVisibility}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-xl right-icon"
                    >
                      {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </div>
                </div>
                <div className="mb-8">
                  <label className="block font-semibold text-navBg mb-2">
                    Confirm New Password
                  </label>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-sm">
                      {errors.confirmPassword}
                    </p>
                  )}
                  <div className="relative">
                    <FaKey className="absolute left-3 top-1/2 transform -translate-y-1/2 text-xl text-gray-400" />
                    <input
                      className="w-full h-12 rounded-lg border border-gray-300 p-2 pl-10"
                      type={confirmPasswordVisible ? "text" : "password"}
                      name="confirmPassword"
                      value={userInfo.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm password"
                    />
                    <span
                      onClick={toggleConfirmPasswordVisibility}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-xl right-icon"
                    >
                      {confirmPasswordVisible ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-10">
              <button
                onClick={handleSaveChanges}
                className="h-10 w-full button-content sm:w-40 rounded-lg bg-navBg text-white font-semibold text-xs hover:bg-blue-700 transition-colors duration-300"
                disabled={loading}
              >
                {loading ? (
                  <RotatingLines
                    strokeColor="#fff"
                    strokeWidth="5"
                    animationDuration="0.75"
                    width="20"
                    visible={true}
                  />
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageProfile;
